// src/modules/Admin/pages/Leads/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import PermissionGate from '../../../../components/PermissionGate';
import {
  FaSearch,
  FaUsers,
  FaEnvelope,
  FaPhone,
  FaBuilding,
  FaCalendarAlt,
  FaEye,
  FaTimes,
  FaCheck,
  FaExclamationCircle,
  FaChevronDown,
  FaList,
  FaColumns,
  FaLock,
  FaPlus,
  FaGlobe,
  FaCommentAlt
} from 'react-icons/fa';
import Pipeline from './Pipeline';

// Funções de mascaramento de dados
const maskEmail = (email) => {
  if (!email || email.length < 5) return '***@***.com';
  const [user, domain] = email.split('@');
  const maskedUser = user.substring(0, 2) + '***';
  const maskedDomain = domain ? domain.substring(0, 3) + '***' : '***';
  return `${maskedUser}@${maskedDomain}`;
};

const maskPhone = (phone) => {
  if (!phone || phone.length < 8) return '(**) *****-****';
  const cleaned = phone.replace(/\D/g, '');
  const ddd = cleaned.substring(0, 2);
  const lastDigits = cleaned.slice(-4);
  return `(${ddd}) *****-${lastDigits}`;
};

// Status configuration
const STATUS_CONFIG = {
  new: { label: 'Novo', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' },
  contacted: { label: 'Contatado', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
  qualified: { label: 'Qualificado', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' },
  proposal: { label: 'Proposta', color: '#06b6d4', bg: 'rgba(6, 182, 212, 0.1)' },
  converted: { label: 'Convertido', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
  lost: { label: 'Perdido', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
};

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111;
`;

const ViewTabs = styled.div`
  display: flex;
  background: #f0f0f0;
  border-radius: 8px;
  padding: 4px;
`;

const ViewTab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#111' : '#666'};
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};

  &:hover {
    color: #111;
  }

  svg {
    font-size: 14px;
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 140px;
`;

const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.$bg || '#f5f5f5'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color || '#666'};
  font-size: 16px;
`;

const StatInfo = styled.div`
  .value {
    font-size: 20px;
    font-weight: 700;
    color: #111;
  }
  .label {
    font-size: 12px;
    color: #666;
  }
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 400px;

  input {
    width: 100%;
    padding: 12px 16px 12px 44px;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    font-size: 14px;
    transition: all 200ms;

    &:focus {
      outline: none;
      border-color: #333;
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
    }
  }

  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    font-size: 16px;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #333;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px 20px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const Td = styled.td`
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
`;

const LeadInfo = styled.div`
  .name {
    font-size: 14px;
    font-weight: 600;
    color: #111;
    margin-bottom: 4px;
  }

  .email {
    font-size: 12px;
    color: #666;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const CompanyText = styled.span`
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 20px;
  background: ${props => STATUS_CONFIG[props.$status]?.bg || '#f5f5f5'};
  color: ${props => STATUS_CONFIG[props.$status]?.color || '#666'};
`;

const StatusDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const StatusButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  background: ${props => STATUS_CONFIG[props.$status]?.bg || '#f5f5f5'};
  color: ${props => STATUS_CONFIG[props.$status]?.color || '#666'};
  transition: all 200ms;

  &:hover {
    opacity: 0.8;
  }

  svg {
    font-size: 10px;
  }
`;

const StatusMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 140px;
  overflow: hidden;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const StatusOption = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: ${props => props.$active ? '#f5f5f5' : 'white'};
  cursor: pointer;
  font-size: 13px;
  color: #333;
  text-align: left;
  transition: background 200ms;

  &:hover {
    background: #f5f5f5;
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.$color || '#999'};
  }
`;

const DateText = styled.span`
  font-size: 13px;
  color: #666;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #666;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #f5f5f5;
    color: #111;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #999;
  font-size: 32px;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111;
`;

const ModalClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #e5e5e5;
    color: #111;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    color: #999;
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const DetailContent = styled.div`
  .label {
    font-size: 12px;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }

  .value {
    font-size: 14px;
    color: #111;
    line-height: 1.5;
  }

  .value a {
    color: #3b82f6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const MessageBox = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;

  .label {
    font-size: 12px;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
  }

  .message {
    font-size: 14px;
    color: #333;
    line-height: 1.6;
    white-space: pre-wrap;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #111;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #333;
    transform: translateY(-1px);
  }

  svg {
    font-size: 12px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  input, select, textarea {
    width: 100%;
    padding: 12px 14px;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    font-size: 14px;
    transition: all 200ms;

    &:focus {
      outline: none;
      border-color: #333;
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
    }

    &::placeholder {
      color: #999;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #f5f5f5;
    border-color: #ddd;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 24px;
  background: #111;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 200ms;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Leads = () => {
  const { user, hasPermission, userRole } = useAuth();
  const [viewMode, setViewMode] = useState('pipeline'); // 'list' or 'pipeline'
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    source: 'manual',
    status: 'new'
  });

  // Verificar permissões
  const canViewFullContact = hasPermission('leads.view_full_contact');
  const canUpdateStatus = hasPermission('leads.update_status');
  const canViewAll = hasPermission('leads.view_all');
  const canCreate = hasPermission('leads.create');

  useEffect(() => {
    fetchLeads();
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchLeads = async () => {
    try {
      let query = supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      // Se não pode ver todos, filtrar apenas os atribuídos ao usuário
      if (!canViewAll && user?.email) {
        query = query.eq('assigned_to', user.email);
      }

      const { data, error } = await query;

      if (error) throw error;
      setLeads(data || []);
    } catch (error) {
      console.error('Erro ao buscar leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', leadId);

      if (error) throw error;

      setLeads(leads.map(lead =>
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));
      setOpenDropdownId(null);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status. Tente novamente.');
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();

    if (!newLead.name) {
      alert('Nome é obrigatório.');
      return;
    }

    setCreating(true);
    try {
      const { data, error } = await supabase
        .from('leads')
        .insert([{
          name: newLead.name,
          email: newLead.email || null,
          phone: newLead.phone || null,
          company: newLead.company || null,
          message: newLead.message || null,
          source: newLead.source || 'manual',
          status: newLead.status || 'new',
          assigned_to: user?.email || null,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      // Adiciona o novo lead no início da lista
      setLeads([data, ...leads]);

      // Limpa o formulário e fecha o modal
      setNewLead({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        source: 'manual',
        status: 'new'
      });
      setShowCreateModal(false);
    } catch (error) {
      console.error('Erro ao criar lead:', error);
      alert(`Erro ao criar lead: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setCreating(false);
    }
  };

  const openCreateModal = () => {
    setNewLead({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: '',
      source: 'manual',
      status: 'new'
    });
    setShowCreateModal(true);
  };

  const filteredLeads = leads.filter(lead => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      lead.name?.toLowerCase().includes(searchLower) ||
      lead.email?.toLowerCase().includes(searchLower) ||
      lead.company?.toLowerCase().includes(searchLower);
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Stats calculation
  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    qualified: leads.filter(l => l.status === 'qualified').length,
    converted: leads.filter(l => l.status === 'converted').length,
  };

  const toggleDropdown = (e, leadId) => {
    e.stopPropagation();
    setOpenDropdownId(openDropdownId === leadId ? null : leadId);
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Title>Leads</Title>
          <ViewTabs>
            <ViewTab
              $active={viewMode === 'pipeline'}
              onClick={() => setViewMode('pipeline')}
            >
              <FaColumns /> Pipeline
            </ViewTab>
            <ViewTab
              $active={viewMode === 'list'}
              onClick={() => setViewMode('list')}
            >
              <FaList /> Lista
            </ViewTab>
          </ViewTabs>
        </HeaderLeft>
        {canCreate && (
          <AddButton onClick={openCreateModal}>
            <FaPlus /> Novo Lead
          </AddButton>
        )}
      </Header>

      {/* Pipeline View */}
      {viewMode === 'pipeline' ? (
        <Pipeline />
      ) : (
        <>
          {/* List View */}
          <StatsRow>
            <StatCard>
              <StatIcon $bg="rgba(59, 130, 246, 0.1)" $color="#3b82f6">
                <FaUsers />
              </StatIcon>
              <StatInfo>
                <div className="value">{stats.total}</div>
                <div className="label">Total</div>
              </StatInfo>
            </StatCard>
            <StatCard>
              <StatIcon $bg="rgba(59, 130, 246, 0.1)" $color="#3b82f6">
                <FaExclamationCircle />
              </StatIcon>
              <StatInfo>
                <div className="value">{stats.new}</div>
                <div className="label">Novos</div>
              </StatInfo>
            </StatCard>
            <StatCard>
              <StatIcon $bg="rgba(139, 92, 246, 0.1)" $color="#8b5cf6">
                <FaCheck />
              </StatIcon>
              <StatInfo>
                <div className="value">{stats.qualified}</div>
                <div className="label">Qualificados</div>
              </StatInfo>
            </StatCard>
            <StatCard>
              <StatIcon $bg="rgba(16, 185, 129, 0.1)" $color="#10b981">
                <FaCheck />
              </StatIcon>
              <StatInfo>
                <div className="value">{stats.converted}</div>
                <div className="label">Convertidos</div>
              </StatInfo>
            </StatCard>
          </StatsRow>

          <FiltersBar>
            <SearchInput>
              <FaSearch />
              <input
                type="text"
                placeholder="Buscar por nome, email ou empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchInput>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Todos os status</option>
              <option value="new">Novos</option>
              <option value="contacted">Contatados</option>
              <option value="qualified">Qualificados</option>
              <option value="proposal">Proposta</option>
              <option value="converted">Convertidos</option>
              <option value="lost">Perdidos</option>
            </FilterSelect>
          </FiltersBar>

          <Card>
        {loading ? (
          <EmptyState>
            <EmptyText>Carregando...</EmptyText>
          </EmptyState>
        ) : filteredLeads.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <FaUsers />
            </EmptyIcon>
            <EmptyTitle>
              {leads.length === 0 ? 'Nenhum lead ainda' : 'Nenhum lead encontrado'}
            </EmptyTitle>
            <EmptyText>
              {leads.length === 0
                ? 'Os leads capturados pelo formulário de contato aparecerão aqui.'
                : 'Tente ajustar os filtros ou o termo de busca.'
              }
            </EmptyText>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Lead</Th>
                <Th>Empresa</Th>
                <Th>Status</Th>
                <Th>Data</Th>
                <Th style={{ width: '80px' }}>Ações</Th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead) => (
                <tr key={lead.id}>
                  <Td>
                    <LeadInfo>
                      <div className="name">{lead.name || 'Sem nome'}</div>
                      <div className="email">
                        <FaEnvelope />
                        {canViewFullContact ? lead.email : maskEmail(lead.email)}
                        {!canViewFullContact && <FaLock style={{ fontSize: 10, marginLeft: 4, color: '#999' }} title="Dados protegidos" />}
                      </div>
                    </LeadInfo>
                  </Td>
                  <Td>
                    <CompanyText>
                      {lead.company ? (
                        <>
                          <FaBuilding />
                          {lead.company}
                        </>
                      ) : (
                        <span style={{ color: '#ccc' }}>-</span>
                      )}
                    </CompanyText>
                  </Td>
                  <Td>
                    {canUpdateStatus ? (
                      <StatusDropdown>
                        <StatusButton
                          $status={lead.status}
                          onClick={(e) => toggleDropdown(e, lead.id)}
                        >
                          {STATUS_CONFIG[lead.status]?.label || lead.status}
                          <FaChevronDown />
                        </StatusButton>
                        <StatusMenu $isOpen={openDropdownId === lead.id}>
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                            <StatusOption
                              key={key}
                              $active={lead.status === key}
                              $color={config.color}
                              onClick={() => updateLeadStatus(lead.id, key)}
                            >
                              <span className="dot" />
                              {config.label}
                            </StatusOption>
                          ))}
                        </StatusMenu>
                      </StatusDropdown>
                    ) : (
                      <StatusBadge $status={lead.status}>
                        {STATUS_CONFIG[lead.status]?.label || lead.status}
                      </StatusBadge>
                    )}
                  </Td>
                  <Td>
                    <DateText>{formatDate(lead.created_at)}</DateText>
                  </Td>
                  <Td>
                    <ActionsCell>
                      <ActionButton
                        onClick={() => setSelectedLead(lead)}
                        title="Ver detalhes"
                      >
                        <FaEye />
                      </ActionButton>
                    </ActionsCell>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
          </Card>
        </>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <ModalOverlay onClick={() => setSelectedLead(null)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Detalhes do Lead</ModalTitle>
              <ModalClose onClick={() => setSelectedLead(null)}>
                <FaTimes />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <DetailRow>
                <FaUsers />
                <DetailContent>
                  <div className="label">Nome</div>
                  <div className="value">{selectedLead.name || 'Não informado'}</div>
                </DetailContent>
              </DetailRow>
              <DetailRow>
                <FaEnvelope />
                <DetailContent>
                  <div className="label">Email {!canViewFullContact && <FaLock style={{ fontSize: 10, marginLeft: 4, color: '#999' }} />}</div>
                  <div className="value">
                    {canViewFullContact ? (
                      <a href={`mailto:${selectedLead.email}`}>{selectedLead.email}</a>
                    ) : (
                      <span style={{ color: '#666' }}>{maskEmail(selectedLead.email)}</span>
                    )}
                  </div>
                </DetailContent>
              </DetailRow>
              {selectedLead.phone && (
                <DetailRow>
                  <FaPhone />
                  <DetailContent>
                    <div className="label">Telefone {!canViewFullContact && <FaLock style={{ fontSize: 10, marginLeft: 4, color: '#999' }} />}</div>
                    <div className="value">
                      {canViewFullContact ? (
                        <a href={`tel:${selectedLead.phone}`}>{selectedLead.phone}</a>
                      ) : (
                        <span style={{ color: '#666' }}>{maskPhone(selectedLead.phone)}</span>
                      )}
                    </div>
                  </DetailContent>
                </DetailRow>
              )}
              {selectedLead.company && (
                <DetailRow>
                  <FaBuilding />
                  <DetailContent>
                    <div className="label">Empresa</div>
                    <div className="value">{selectedLead.company}</div>
                  </DetailContent>
                </DetailRow>
              )}
              <DetailRow>
                <FaCalendarAlt />
                <DetailContent>
                  <div className="label">Data de Cadastro</div>
                  <div className="value">{formatDate(selectedLead.created_at)}</div>
                </DetailContent>
              </DetailRow>
              {selectedLead.source && (
                <DetailRow>
                  <FaExclamationCircle />
                  <DetailContent>
                    <div className="label">Origem</div>
                    <div className="value">{selectedLead.source}</div>
                  </DetailContent>
                </DetailRow>
              )}
              {selectedLead.message && (
                <MessageBox>
                  <div className="label">Mensagem</div>
                  <div className="message">{selectedLead.message}</div>
                </MessageBox>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Create Lead Modal */}
      {showCreateModal && (
        <ModalOverlay onClick={() => setShowCreateModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <ModalHeader>
              <ModalTitle>Novo Lead</ModalTitle>
              <ModalClose onClick={() => setShowCreateModal(false)}>
                <FaTimes />
              </ModalClose>
            </ModalHeader>
            <form onSubmit={handleCreateLead}>
              <ModalBody>
                <FormRow>
                  <FormGroup>
                    <label>Nome *</label>
                    <input
                      type="text"
                      placeholder="Nome do contato"
                      value={newLead.name}
                      onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Email</label>
                    <input
                      type="email"
                      placeholder="email@exemplo.com (opcional)"
                      value={newLead.email}
                      onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <label>Telefone</label>
                    <input
                      type="tel"
                      placeholder="(00) 00000-0000"
                      value={newLead.phone}
                      onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Empresa</label>
                    <input
                      type="text"
                      placeholder="Nome da empresa"
                      value={newLead.company}
                      onChange={(e) => setNewLead({ ...newLead, company: e.target.value })}
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <label>Origem</label>
                    <select
                      value={newLead.source}
                      onChange={(e) => setNewLead({ ...newLead, source: e.target.value })}
                    >
                      <option value="manual">Inserção Manual</option>
                      <option value="website">Website</option>
                      <option value="indicacao">Indicação</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="evento">Evento</option>
                      <option value="outro">Outro</option>
                    </select>
                  </FormGroup>
                  <FormGroup>
                    <label>Status Inicial</label>
                    <select
                      value={newLead.status}
                      onChange={(e) => setNewLead({ ...newLead, status: e.target.value })}
                    >
                      <option value="new">Novo</option>
                      <option value="contacted">Contatado</option>
                      <option value="qualified">Qualificado</option>
                      <option value="proposal">Proposta</option>
                    </select>
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <label>Observações</label>
                  <textarea
                    placeholder="Informações adicionais sobre o lead..."
                    value={newLead.message}
                    onChange={(e) => setNewLead({ ...newLead, message: e.target.value })}
                    rows={4}
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <CancelButton type="button" onClick={() => setShowCreateModal(false)}>
                  Cancelar
                </CancelButton>
                <SubmitButton type="submit" disabled={creating}>
                  {creating ? 'Salvando...' : 'Criar Lead'}
                </SubmitButton>
              </ModalFooter>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Leads;
